import { Quaternion, Vector3 } from "flavr-math";
import { LoadMeshMessage, MeshLoadedMessage } from "../../../messages/rendering/RendererMessages";
import { Color, EdgesGeometry, LineBasicMaterial, LineSegments, Mesh, Object3D } from "three";
import { MaterialQualitySystem } from "../material/MaterialQualitySystem";
import { meshLoadingTracker } from "./MeshLoadingTracker";
export class MeshInstance {
    constructor(renderer, onMeshLoadedCallback, onMeshStartLoadingCallback, shadowOwnerModelUID) {
        this.renderer = renderer;
        this.onMeshLoadedCallback = onMeshLoadedCallback;
        this.onMeshStartLoadingCallback = onMeshStartLoadingCallback;
        this.shadowOwnerModelUID = shadowOwnerModelUID;
        this.isVisible = true;
        this.meshName = "";
        this.castShadow = false;
        this.receiveShadow = false;
        /** A scalar Scale being applied to the mesh after loading is complete. Used when importing cad-data that is scaled to millimeters or centimeters. */
        this.scale = 1;
        this.offsetGroup = new Object3D();
        this.applyOutline = false;
        this.outlineColor = "";
        this.cloneMaterials = false;
        this.offsetGroup.matrixAutoUpdate = false;
    }
    get isLoaded() {
        return !!this.group;
    }
    destroy() {
        this.offsetGroup.parent && this.offsetGroup.parent.remove(this.offsetGroup);
        this.removeMesh();
        this.meshName = "";
        this.subMeshVisibilities = undefined;
    }
    removeMesh() {
        if (this.group === undefined)
            return;
        this.group.parent && this.group.parent.remove(this.group);
        this.group = undefined;
        if (this.castShadow) {
            this.triggerShadowUpdate();
        }
    }
    setCastShadow(value) {
        this.castShadow = value;
        if (this.group) {
            this.group.traverse((child) => {
                child.castShadow = this.castShadow;
            });
            if (this.castShadow) {
                this.triggerShadowUpdate();
            }
        }
    }
    getCastShadow() {
        return this.castShadow;
    }
    setReceiveShadow(value) {
        this.receiveShadow = value;
        if (this.group) {
            this.group.traverse((child) => {
                child.receiveShadow = this.receiveShadow;
            });
        }
    }
    setVisible(visible) {
        let changed = (this.isVisible = visible);
        this.offsetGroup.visible = visible;
        if (this.group) {
            changed = this.group.visible !== visible;
            this.group.visible = visible;
            if (changed && this.castShadow) {
                this.triggerShadowUpdate();
            }
        }
        return changed;
    }
    getVisible() {
        return this.isVisible;
    }
    getGeometry() {
        if (!this.group)
            return;
        if (this.group instanceof Mesh) {
            return {
                geometry: this.group.geometry,
                mesh: this.group
            };
        }
        // Breadth-first search
        const stack = [...this.group.children];
        for (const child of stack) {
            if (child instanceof Mesh) {
                return {
                    geometry: child.geometry,
                    mesh: child
                };
            }
            stack.push(...child.children);
        }
        return undefined;
    }
    /**
     * @param meshName
     * @param addBarycentricCoordinates Used to calculate fast wireframe-shaders.
     * @param pieceName If the "mesh" is part of a bigger thing.
     * @param scale A scalar Scale being applied to the mesh after loading is complete. Used when importing cad-data that is scaled to millimeters or centimeters.
     * @param applyOutline Indicates whether the mesh gets an outline
     * @param outlineColor
     * @param cloneMaterials
     */
    load(meshName, addBarycentricCoordinates = false, pieceName, scale = 1, applyOutline, outlineColor, cloneMaterials) {
        if (this.meshName === meshName)
            return false;
        this.meshName = meshName;
        this.scale = scale;
        this.applyOutline = applyOutline;
        this.outlineColor = outlineColor;
        this.cloneMaterials = cloneMaterials;
        if (this.onMeshStartLoadingCallback !== undefined) {
            this.onMeshStartLoadingCallback(this);
        }
        const loadId = meshLoadingTracker.beginLoad(meshName, pieceName, this.shouldTrackMeshLoadForShadow());
        this.renderer
            .getConnection()
            .askByMessageType(new LoadMeshMessage(meshName, addBarycentricCoordinates, pieceName), MeshLoadedMessage)
            .then((msg) => {
            this.handleMeshLoadedMessage(msg);
            this.renderer.getSystem(MaterialQualitySystem).changeMeshMaterialsInSceneGraph();
        })
            .catch((messages) => {
            console.error(`Failed on load mesh ${meshName}`, messages);
        })
            .finally(() => {
            meshLoadingTracker.endLoad(loadId);
        });
        return true;
    }
    handleMeshLoadedMessage(msg) {
        if (this.meshName !== msg.meshName)
            return;
        this.removeMesh(); // remove old mesh if necessary.
        if (msg.pieceName) {
            const wq = new Quaternion();
            const ws = new Vector3();
            msg.mesh.getWorldQuaternion(wq);
            msg.mesh.getWorldScale(ws);
            // ToDo: Hack, because the recursive parameter is missing in mesh object
            const children = msg.mesh.children;
            msg.mesh.children = [];
            this.group = msg.mesh.clone(false);
            msg.mesh.children = children;
            this.group.position.setScalar(0);
            this.group.quaternion.identity();
            this.group.scale.setScalar(1);
        }
        else {
            this.group = msg.mesh.clone();
        }
        this.group.visible = this.isVisible;
        if (msg.pieceName) {
            this.group.position.setScalar(0);
        }
        this.group.traverse((node) => {
            var _a;
            node.updateMatrix();
            node.matrixWorld.copy(node.matrix);
            node.matrixAutoUpdate = false;
            node.frustumCulled = (_a = this.frustumCulled) !== null && _a !== void 0 ? _a : true;
            node.receiveShadow = this.receiveShadow;
            node.castShadow = this.castShadow;
            if (node instanceof Mesh && this.cloneMaterials) {
                if (!Array.isArray(node.material) && node.material) {
                    node.material = node.material.clone();
                }
                if (Array.isArray(node.material)) {
                    const materialArray = [];
                    for (const material of node.material) {
                        materialArray.push(material.clone());
                    }
                    node.material = materialArray;
                }
            }
            // Add optional outline shader
            if (node instanceof Mesh && this.applyOutline) {
                let color = new Color(0xff8800);
                if (this.outlineColor !== "") {
                    color = new Color(this.outlineColor);
                }
                const edges = new EdgesGeometry(node.geometry);
                const line = new LineSegments(edges, new LineBasicMaterial({ color: color.getHex(), linewidth: 1 }));
                node.add(line);
            }
        });
        // Scale each node with the scale parameter
        // TODO: The 1e-16 hack shouldn't be necessary. Somehow the changes introduced with the new ui caused vertexScaleFactor to be a ridiculously small number.
        // Unfortunately we couldn't find out why.
        if (this.scale !== undefined && (this.scale > 1e-16 || this.scale === 0)) {
            this.group.scale.set(this.scale, this.scale, this.scale);
            this.group.updateMatrix();
        }
        if (this.subMeshVisibilities) {
            for (const it in this.subMeshVisibilities) {
                const subMesh = this.group.getObjectByName(it);
                subMesh && (subMesh.visible = this.subMeshVisibilities[it]);
            }
        }
        this.setVisible(this.isVisible);
        this.offsetGroup.add(this.group);
        this.offsetGroup.name = `MeshInstance_${this.group.name}`;
        this.offsetGroup.updateMatrix();
        this.offsetGroup.updateMatrixWorld();
        this.onMeshLoadedCallback(this.offsetGroup, this);
    }
    /** check if a given sub mesh exists. Good for sanity-checks. */
    hasSubmesh(subMeshName) {
        if (this.group === undefined) {
            console.warn("Searching for a submesh within " + this.meshName + ", but not even the mesh itself was defined!");
            return false;
        }
        return this.group.getObjectByName(subMeshName) !== undefined;
    }
    setSubMeshTransform(subMeshName, position, orientation, scale) {
        const subMesh = this.group ? this.group.getObjectByName(subMeshName) : undefined;
        if (subMesh !== undefined) {
            subMesh.position.fromArray(position);
            subMesh.quaternion.fromArray(orientation !== null && orientation !== void 0 ? orientation : [0, 0, 0, 1]);
            subMesh.scale.fromArray(scale !== null && scale !== void 0 ? scale : [1, 1, 1]);
            subMesh.updateMatrix();
            subMesh.updateMatrixWorld(true);
            if (this.castShadow && this.isVisible) {
                this.triggerShadowUpdate();
            }
        }
    }
    setSubMeshVisibility(subMeshName, visible) {
        if (this.group) {
            const subMesh = this.group.getObjectByName(subMeshName);
            if (!subMesh)
                return;
            const changed = subMesh.visible !== visible;
            subMesh.visible = visible;
            if (this.castShadow && changed) {
                this.triggerShadowUpdate();
            }
        }
        else if (this.subMeshVisibilities) {
            this.subMeshVisibilities[subMeshName] = visible;
        }
    }
    updateSubMeshVisibilities(subMeshVisibilities) {
        if (this.group) {
            this.subMeshVisibilities = undefined;
            if (subMeshVisibilities) {
                for (const it in subMeshVisibilities) {
                    const subMesh = this.group.getObjectByName(it);
                    subMesh && (subMesh.visible = subMeshVisibilities[it]);
                }
            }
        }
        else {
            this.subMeshVisibilities = subMeshVisibilities;
        }
    }
    updateTransform(matrix) {
        this.offsetGroup.matrix.copy(matrix);
        this.offsetGroup.matrixWorldNeedsUpdate = true;
        if (!this.group)
            return;
        if (this.castShadow) {
            this.triggerShadowUpdate();
        }
    }
    shouldTrackMeshLoadForShadow() {
        if (this.shadowOwnerModelUID === undefined)
            return false;
        return this.renderer.isModelInCurrentRoom(this.shadowOwnerModelUID);
    }
    triggerShadowUpdate() {
        if (this.shadowOwnerModelUID !== undefined) {
            this.renderer.updateShadowForModel(this.shadowOwnerModelUID);
            return;
        }
        this.renderer.updateShadow();
    }
    traverseMesh(traverseCallback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh) {
                traverseCallback(child);
            }
        });
    }
    traverseMeshByObjectName(name, traverseCallback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh && child.name === name) {
                traverseCallback(child);
            }
        });
    }
    traverseMaterial(type, callback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh) {
                const mesh = child;
                if (Array.isArray(mesh.material)) {
                    for (const mat of mesh.material) {
                        if (mat instanceof type) {
                            callback(mat, mesh);
                        }
                    }
                }
                else if (mesh.material instanceof type) {
                    callback(mesh.material, mesh);
                }
            }
        });
    }
    traverseMaterialByName(name, callback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh) {
                const mesh = child;
                if (Array.isArray(mesh.material)) {
                    for (let i = 0, len = mesh.material.length; i < len; ++i) {
                        const mat = mesh.material[i];
                        if (mat.name === name) {
                            callback(mat, mesh, i);
                        }
                    }
                }
                else if (mesh.material.name === name) {
                    callback(mesh.material, mesh);
                }
            }
        });
    }
    findMaterialByName(name) {
        if (!this.group)
            return undefined;
        const objects = [];
        objects.push(this.group);
        objects.push(...this.group.children);
        for (const obj of objects) {
            if (obj.isMesh) {
                const mesh = obj;
                if (Array.isArray(mesh.material)) {
                    for (const mat of mesh.material) {
                        if (mat.name === name) {
                            return mat;
                        }
                    }
                }
                else if (mesh.material.name === name) {
                    return mesh.material;
                }
            }
            objects.push(...obj.children);
        }
        return undefined;
    }
    findSubObjectByName(name) {
        if (!this.group)
            return undefined;
        return this.group.getObjectByName(name);
    }
}
